"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import InputPasswordWithReveal from "@/components/customs/InputPasswordWithReveal";
import Link from "next/link";
import useLogin, { EmailFormValues } from "./useLogin";
import { SearchParamsWrapper } from "@/components/customs/SearchParamsWrapper";
import { SubmitHandler, UseFormReturn } from "react-hook-form";
import { SupportedLanguage } from "@/lib/shared";
import lang from "./lang.json";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useSearchParams } from "next/navigation";

function FormLoginWithEmailContent({ locale }: { locale: SupportedLanguage }) {
  const searchParams = useSearchParams();
  // Preserve redirect param to pass through forgot-password flow
  const redirect = searchParams.get("redirect");

  // Get the login hook with email tab (handles redirect internally)
  const {
    form,
    onSubmit: originalOnSubmit,
    isLoading,
  } = useLogin({ tab: "email" });

  // Safely cast the form to the email form type
  const typedForm = form as UseFormReturn<EmailFormValues>;

  // Create a type-safe submit handler
  const handleSubmit: SubmitHandler<EmailFormValues> = (data) => {
    // Since we know we're in the email tab, we can safely call the original onSubmit
    // TypeScript doesn't know this is safe, but we do based on the implementation of useLogin
    return (originalOnSubmit as (values: EmailFormValues) => Promise<void>)(
      data
    );
  };

  return (
    <Form {...typedForm}>
      <form
        onSubmit={typedForm.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        <FormField
          control={typedForm.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["role"][locale]}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Update URL with selected role while preserving other params
                    const params = new URLSearchParams(searchParams.toString());
                    params.set("role", value);
                    const newUrl = `${window.location.pathname}?${params.toString()}`;
                    window.history.replaceState({}, "", newUrl);
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Personal" id="Personal" />
                      <Label htmlFor="Personal">
                        {lang["role-personal"][locale]}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Company" id="Company" />
                      <Label htmlFor="Company">
                        {lang["role-company"][locale]}
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={typedForm.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["email"][locale]}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Ex: Mohamed@gmail.com"
                  {...field}
                  value={field.value as string}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <InputPasswordWithReveal
          form={typedForm}
          name="password"
          label={lang["password"][locale]}
          placeholder={lang["placeholder-password"][locale]}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox id="remember" />
            <label
              htmlFor="remember"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              {lang["remember-me"][locale]}
            </label>
          </div>

          <Link
            href={`/${locale}/auth/forgot-password?tab=email${
              redirect ? `&redirect=${encodeURIComponent(redirect)}` : ""
            }`}
            className="text-sm text-primary hover:underline"
          >
            {lang["forgot-password"][locale]}
          </Link>
        </div>

        <Button
          rounded="full"
          disabled={isLoading}
          type="submit"
          variant="default"
          className="w-full py-6 h-12 mt-6"
        >
          {isLoading ? lang["logging-in"][locale] : lang["log-in"][locale]}
        </Button>
      </form>
    </Form>
  );
}

function FormLoginWithEmail({ locale }: { locale: SupportedLanguage }) {
  return (
    <SearchParamsWrapper>
      <FormLoginWithEmailContent locale={locale} />
    </SearchParamsWrapper>
  );
}

export default FormLoginWithEmail;
